import { api, listarTudo } from '../../services/api';
import { db } from '../db';
import { enfileirarMudanca, formatarParaTela } from '../engine';
import { SYNC_SCHEMA } from '../schema';
import { ehUuid, gerarUuid } from '../uuid';

// Fábrica de repositório: dado uma chave do SYNC_SCHEMA, devolve
// {listar, buscarUm, criar, atualizar, excluir} com a MESMA forma de
// resposta que `api.get/post/put/delete` já tinham (response.status,
// response.data) — pensado para trocar `api.get(...)` por
// `repositorio.listar(...)` em cada página sem precisar reescrever o resto
// do código que já lida com a resposta.
//
// Online: chama a API normalmente, sem nenhuma mudança de comportamento —
// é o caminho de sempre. Offline: lê/escreve no Dexie e enfileira a mudança
// para quando a conexão voltar (só quando `escritaOffline` permite; ver
// schema.js).
//
// Limitação conhecida: o Dexie só é atualizado a partir do servidor pelo
// `puxarTudo()` (login e reconexão) — uma sessão online longa sem
// reconectar pode deixar o cache local levemente desatualizado até a
// próxima sincronização. Isso não afeta o que aparece na tela enquanto
// online (que sempre lê direto da API), só o fallback quando cair a conexão.
export function criarRepositorio(chave) {
  const { tabela, endpoint, escritaOffline } = SYNC_SCHEMA[chave];

  async function localPorIdentificador(id) {
    return ehUuid(id) ? db[tabela].get(id) : db[tabela].where('id').equals(Number(id)).first();
  }

  async function listar(queryString = '') {
    if (navigator.onLine) {
      return listarTudo(`${endpoint}/${queryString}`);
    }
    const registros = await db[tabela].filter((r) => !r.excluido_em).toArray();
    const formatados = await Promise.all(registros.map((r) => formatarParaTela(chave, r)));
    return { data: formatados };
  }

  async function buscarUm(id) {
    if (!ehUuid(id) && navigator.onLine) {
      return api.get(`${endpoint}/${id}/`);
    }
    const registro = await localPorIdentificador(id);
    if (!registro) throw new Error('Registro não encontrado localmente (offline).');
    return { data: await formatarParaTela(chave, registro) };
  }

  async function criar(payload) {
    if (navigator.onLine) {
      return api.post(`${endpoint}/`, payload);
    }
    if (!escritaOffline) {
      throw new Error('Sem conexão — este cadastro exige internet.');
    }
    const uuid = gerarUuid();
    await enfileirarMudanca({ chave, op: 'create', uuid, payload });
    const registro = await db[tabela].get(uuid);
    return { status: 201, data: await formatarParaTela(chave, registro) };
  }

  async function atualizar(id, payload) {
    if (!ehUuid(id) && navigator.onLine) {
      return api.put(`${endpoint}/${id}/`, payload);
    }
    if (!escritaOffline) {
      throw new Error('Sem conexão — esta edição exige internet.');
    }
    const anterior = await localPorIdentificador(id);
    if (!anterior) throw new Error('Registro não encontrado localmente (offline).');
    await enfileirarMudanca({
      chave, op: 'update', uuid: anterior.uuid, payload, baseAtualizadoEm: anterior.atualizado_em,
    });
    const registro = await db[tabela].get(anterior.uuid);
    return { status: 200, data: await formatarParaTela(chave, registro) };
  }

  async function excluir(id) {
    if (!ehUuid(id) && navigator.onLine) {
      return api.delete(`${endpoint}/${id}/`);
    }
    if (!escritaOffline) {
      throw new Error('Sem conexão — esta exclusão exige internet.');
    }
    const anterior = await localPorIdentificador(id);
    if (!anterior) throw new Error('Registro não encontrado localmente (offline).');
    await enfileirarMudanca({
      chave, op: 'delete', uuid: anterior.uuid, payload: {}, baseAtualizadoEm: anterior.atualizado_em,
    });
  }

  return { listar, buscarUm, criar, atualizar, excluir };
}
